// intakeAnimalForm — DEPENDENCY-FREE pure helpers for the pre-lease intake's
// animals section (IntakeFormPage), factored out of the page so the rules are
// unit-testable (WEL-173).
//
// The questions are the SAME ones the Animal Addendum form asks — both forms
// edit lib/animalDraft.ts's answers and render components/AnimalQuestionFields
// — so an animal listed at application time and one added mid-tenancy are
// asked, and stored, alike. What this module adds is the intake's own shape:
// the roster rows carry duplicate / removed flags and who a row is delegated to.
//
// Imports: type-only from ./intakeClient (erased — that module reads
// import.meta.env), plus the dependency-free ./animalQuestions and ./animalDraft.

import type { AnimalInput, IntakeAnimalView, IntakeRole } from './intakeClient';
import {
  animalAnswerReasons,
  blankAnimalAnswers,
  draftToWireAnswers,
  storedToAnimalAnswers,
  type AnimalAnswerDraft,
} from './animalDraft';

/** One roster row as the intake form edits it. */
export interface IntakeAnimalDraft extends AnimalAnswerDraft {
  id: string;
  duplicateOfId: string | null;
  removed: boolean;
  sourceApplicantName?: string;
  /** Who the primary delegated this animal to (they answer for it). */
  assignedToApplicantId?: string;
  /** Who last answered for it; a delegate's answer outranks the primary's. */
  authoredBy?: 'primary' | 'delegate' | 'staff';
  /**
   * The row came from the server's view (as opposed to "+ Add an animal"). A
   * delegate's view is exactly their assigned animals, so for a delegate this is
   * "assigned to me" — the set the API holds them to.
   */
  fromServer: boolean;
}

/** The server's view of one animal → an editable row. */
export function intakeAnimalToDraft(a: IntakeAnimalView): IntakeAnimalDraft {
  return {
    id: a.id,
    ...storedToAnimalAnswers(a),
    duplicateOfId: a.duplicateOfId ?? null,
    removed: a.removed ?? false,
    ...(a.sourceApplicantName ? { sourceApplicantName: a.sourceApplicantName } : {}),
    ...(a.assignedToApplicantId ? { assignedToApplicantId: a.assignedToApplicantId } : {}),
    ...(a.authoredBy ? { authoredBy: a.authoredBy } : {}),
    fromServer: true,
  };
}

/** A blank row for "+ Add an animal". */
export function newIntakeAnimalDraft(id: string): IntakeAnimalDraft {
  return { id, ...blankAnimalAnswers(), duplicateOfId: null, removed: false, fromServer: false };
}

/** A row → the wire: only the answers its kind is asked, plus the roster flags. */
export function intakeDraftToAnimalInput(d: IntakeAnimalDraft): AnimalInput {
  return {
    id: d.id,
    ...draftToWireAnswers(d),
    ...(d.duplicateOfId !== null ? { duplicateOfId: d.duplicateOfId } : {}),
    ...(d.removed ? { removed: true } : {}),
  };
}

/**
 * Whether THIS applicant must finish this row before submitting — the SAME rule
 * the API applies to the roster it would record (`animalsAnsweredBy`,
 * apps/api/src/lib/leaseIntakeForm.ts; WEL-173 review P2):
 *   • never a removed row or one marked as a duplicate, never a staff row;
 *   • a DELEGATE answers for exactly the rows their view showed — their assigned
 *     animals (a row they add themselves is not one the server will keep);
 *   • the PRIMARY answers for the rest, except a row a delegate has answered or
 *     one delegated to someone else.
 * "No animals" is handled by the submit (it sends no rows, and the API resolves
 * a delegate's assigned animals as removed), not here.
 */
export function intakeAnimalIsJudged(
  d: IntakeAnimalDraft,
  me: { applicantId: string; role: IntakeRole },
): boolean {
  if (d.removed || d.duplicateOfId) return false;
  if (d.authoredBy === 'staff') return false;
  if (me.role === 'delegate') return d.fromServer;
  if (d.authoredBy === 'delegate') return false;
  return !d.assignedToApplicantId || d.assignedToApplicantId === me.applicantId;
}

/**
 * Why the animals section cannot be submitted yet — one plain sentence per
 * missing answer, for the rows this applicant answers for ("Tell us your dog's
 * breed.", "What color is Mochi?"). A missing license is never a reason.
 */
export function intakeAnimalReasons(
  drafts: readonly IntakeAnimalDraft[],
  me: { applicantId: string; role: IntakeRole },
): string[] {
  return animalAnswerReasons(drafts.filter((d) => intakeAnimalIsJudged(d, me)));
}

/**
 * The animals list the form sends. "No animals" sends NO rows — for the primary
 * a genuine declared-none (WEL-54), and for a delegate the answer the API
 * resolves by marking their assigned animals removed (WEL-173 review P2) — so
 * "No" can never leave an assigned animal standing unanswered. "Yes" sends
 * every row, removed and duplicate flags included.
 */
export function intakeAnimalsToSend(
  hasAnimals: boolean | null,
  drafts: readonly IntakeAnimalDraft[],
): AnimalInput[] {
  return hasAnimals === false ? [] : drafts.map(intakeDraftToAnimalInput);
}
